<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import LocalStorage from '@/controller/LocalStorage.js';
import MazInput from 'maz-ui/components/MazInput';
import SearchList from '@/components/common/navbars/components/SearchList.vue';
import { useCommunicatorStore } from '@/stores/communicator.js';
import { storeToRefs } from "pinia";
const communicatorStore = useCommunicatorStore();
const { getGlobalSearchItems: searchList } = storeToRefs(communicatorStore);
const { t } = useI18n();
const userKey = ref(LocalStorage.getKey());
const userColor = ref(LocalStorage.getColor());
const searchText = ref('');
const dynamicColor = computed(() => isFocused.value ? userColor.value : '#000000');
const focused = ref(false);
const isFocused = computed(() => focused.value);
const isMouseDownOnSearchList = ref(false);
const globalSearchGlass = ref(null);
const searchInput = ref(null);
const showSearchList = ref(true);
const isTouchDevice = ref(false);
const isVirtualKeyboardVisible = ref(false);
const preventClearDuringKeyboardTransition = ref(false);

function checkTouchDevice() {
  isTouchDevice.value = 
    ('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0) ||
    (navigator.msMaxTouchPoints > 0)
  ;
}

function search(){
    communicatorStore.fetchSearch({k: userKey.value, filters:encodeURIComponent(searchText.value)});
    if(searchText.value.length < 3){
        showSearchList.value = true;
    }else{
        showSearchList.value = true;
    }
}
function clearList(){
    // Don't clear during keyboard transitions (for touch devices)
    if (preventClearDuringKeyboardTransition.value) {
        return;
    }
    
    focused.value = false;
    setTimeout(() => {
        // Check both keyboard presence and our virtual keyboard flag
        if (isVirtualKeyboardVisible.value || 
            ('virtualKeyboard' in navigator && navigator.virtualKeyboard.boundingRect.height > 0)) {
            return;
        }
        
        searchText.value = '';
        communicatorStore.deleteSearchList();
        showSearchList.value = false;
    }, 300);
}
function handleBlur(event) {
    
    // Skip immediate blur handling on touch devices when keyboard appears
    if (isTouchDevice.value && isVirtualKeyboardVisible.value) {
        return;
    }
    
    if (isTouchDevice.value) {
        // For touch devices, delay the blur handling to prevent virtual keyboard issues
        setTimeout(() => {
            // If virtual keyboard is visible, don't clear
            if (isVirtualKeyboardVisible.value) {
                return;
            }
            
            // Check if input is still the active element or part of active element
            if (document.activeElement === searchInput.value?.$?.refs?.input || 
                isMouseDownOnSearchList.value) {
                return;
            }
            
            clearList();
        }, 500);
    } else {
        // For non-touch devices, keep current behavior
        if (!isMouseDownOnSearchList.value) {
            clearList();
        }
    }
}

function handleMouseDown() {
    isMouseDownOnSearchList.value = true;
}

function handleMouseLeave() {
    isMouseDownOnSearchList.value = false;
}
function handleEsc(){
    if (searchInput.value && searchInput.value.$) {
        searchInput.value.$.refs.input.blur();
    }
    clearList();
}

// Added this function to check if the event target is related to our search component
function isEventFromSearchComponent(event) {
  if (!event || !event.target) return false;
  
  // Check if the event target is the search input or a child of it
  if (searchInput.value && searchInput.value.$el) {
    if (searchInput.value.$el.contains(event.target)) {
      return true;
    }
  }
  
  // Also check if it's from the search list dropdown
  const searchListEl = document.getElementById('searchList');
  if (searchListEl && searchListEl.contains(event.target)) {
    return true;
  }
  
  return false;
}

// Added this function to prevent the search input from stealing focus
function preventFocusStealing() {
  // Listen for focus events on the document
  document.addEventListener('focusin', (event) => {
    // Only proceed if this is a touch device
    if (!isTouchDevice.value) return;
    
    // If the focus event is not from our search component but our input has focus,
    // and the target is an input, textarea, or other focusable element
    if (!isEventFromSearchComponent(event) && 
        document.activeElement === searchInput.value?.$?.refs?.input &&
        (event.target.tagName === 'INPUT' || 
         event.target.tagName === 'TEXTAREA' || 
         event.target.hasAttribute('contenteditable'))) {
      
      // Blur our search input to allow the other element to receive focus
      if (searchInput.value && searchInput.value.$ && searchInput.value.$.refs.input) {
        searchInput.value.$.refs.input.blur();
      }
      
      // Clear the search list if needed
      if (focused.value) {
        clearList();
      }
      
      // Force focus back to the originally clicked element
      setTimeout(() => {
        event.target.focus();
      }, 50);
    }
  }, true); // Use capture phase to handle the event before other handlers
}

// Added this function to handle touches outside the input field to close keyboard
function setupOutsideTouchHandler() {
  if (!isTouchDevice.value) return;
  
  // Handle touches outside the input to dismiss keyboard
  document.addEventListener('touchstart', (event) => {
    // Get the currently focused element
    const activeElement = document.activeElement;
    
    // Only continue if the active element is an input-type element
    const isInputElement = activeElement && (
      (activeElement.tagName === 'INPUT' && !activeElement.id.includes('MazSelect')) || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.hasAttribute('contenteditable')
    );
    
    if (!isInputElement) {
      return; // Not an input element, don't blur anything
    }
    
    // Check if touch was outside of this or any other input
    const isTouchOnInput = (
      event.target.tagName === 'INPUT' || 
      event.target.tagName === 'TEXTAREA' || 
      event.target.hasAttribute('contenteditable') ||
      isEventFromSearchComponent(event)
    );
    
    if (!isTouchOnInput) {
      
      // Blur the active input element
      activeElement.blur();
      
      // If it was our search input, clear the list too
      if (searchInput.value && 
          searchInput.value.$ && 
          searchInput.value.$.refs.input === activeElement) {
        clearList();
      }
      
      // For iOS/iPadOS, explicitly hide the virtual keyboard
      if ('virtualKeyboard' in navigator) {
        navigator.virtualKeyboard.hide();
      }
    }
  }, true);
}

onMounted(() => {
    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);
    
    // Add better handling for touch devices
    if (isTouchDevice.value && searchInput.value && searchInput.value.$el) {
        const inputElement = searchInput.value.$el;
        
        // Add touchstart handler to prevent propagation
        inputElement.addEventListener('touchstart', (e) => {
            // Only prevent propagation if this input is specifically being touched
            // This helps prevent focus stealing from other inputs
            e.stopPropagation();
        }, { passive: false });
        
        // Get the actual input field
        const actualInput = searchInput.value.$.refs.input;
        if (actualInput) {
            // Force focus to remain on input when explicitly tapped
            actualInput.addEventListener('touchend', (e) => {
                // We want to allow the default action for showing the keyboard
                // but we need to make sure blur doesn't hide our list
                preventClearDuringKeyboardTransition.value = true;
                setTimeout(() => {
                    // Only focus if this input was explicitly tapped
                    if (isEventFromSearchComponent(e)) {
                        actualInput.focus();
                    }
                    preventClearDuringKeyboardTransition.value = false;
                }, 100);
            }, { passive: true }); // Make this passive: true to ensure keyboard shows
        }
    }
    
    // Check if Virtual Keyboard API is available
    if ('virtualKeyboard' in navigator) {
        // Listen for virtual keyboard showing/hiding
        navigator.virtualKeyboard.addEventListener('geometrychange', (event) => {
            const rect = event.target.boundingRect;
            
            // More reliable check for keyboard visibility
            const isVisible = rect.height > 0 || rect.width > 0;
            isVirtualKeyboardVisible.value = isVisible;
            
            // Only ensure search input stays focused if it was the one that triggered the keyboard
            if (isVisible && 
                searchInput.value && 
                searchInput.value.$.refs.input && 
                isEventFromSearchComponent({ target: document.activeElement })) {
                
                // Don't clear the list when keyboard appears
                preventClearDuringKeyboardTransition.value = true;
                
                // Ensure input retains focus only if it was the intended target
                setTimeout(() => {
                    searchInput.value.$.refs.input.focus();
                    preventClearDuringKeyboardTransition.value = false;
                }, 50);
            }
        });
        
        // Optionally enable automatic showing of virtual keyboard
        if (isTouchDevice.value) {
            navigator.virtualKeyboard.overlaysContent = true;
        }
    }
    
    // Initialize focus stealing prevention
    preventFocusStealing();
    
    // Initialize outside touch handler
    setupOutsideTouchHandler();
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', checkTouchDevice);
    
    // Clean up touch device event listeners
    if (isTouchDevice.value && searchInput.value && searchInput.value.$el) {
        const inputElement = searchInput.value.$el;
        inputElement.removeEventListener('touchstart', () => {});
        
        const actualInput = searchInput.value.$.refs.input;
        if (actualInput) {
            actualInput.removeEventListener('touchend', () => {});
        }
    }
    
    // Clean up Virtual Keyboard API event listener
    if ('virtualKeyboard' in navigator) {
        navigator.virtualKeyboard.removeEventListener('geometrychange', () => {});
    }
});
</script>

<template>
    <div class="global-searcher" ref="globalSearchGlass">
        <MazInput
            ref="searchInput"
            v-model="searchText"
            :label="''"
            color="white"
            class="yauty-input"
            autocomplete="off"
            type="text"
            size="sm"
            rounded-size="full"
            inputmode="search"
            @keyup="search"
            @focus="() => focused = true"
            @blur="handleBlur"
            @keyup.esc="handleEsc"
            :placeholder="t('search.placeholder')"
            >
            <template #left-icon>
                <svg width="16" height="16" data-v-56e02ab8="" class="svg-inline--fa fa-magnifying-glass searchbox-icon" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="magnifying-glass" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path class="" fill="currentColor" d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"></path></svg>
            </template>
        </MazInput>
        <SearchList v-if="showSearchList" :focused="isFocused" @clear="clearList" @mousedown="handleMouseDown" @mouseleave="handleMouseLeave"/>
    </div>
</template>

<style lang="scss">
.global-searcher{
    .yauty-input{
        width: 100%;
        border-color: white !important;
        border: unset !important;
        border: none !important;
        .m-input-wrapper{
            border: none;
            padding-left: 20px;
            .m-input-wrapper-left svg{
                color: v-bind(dynamicColor);
            }
            .m-input-wrapper-right{}
        }
        @media (max-width: 576px) {
            z-index: 3;
        }
    }
}
</style>